<template>
  <b-dropdown
    class="grouped-select"
    boundary="window"
    ref="groupedDropdown"
    :class="{ 'grouped-select__invalid': state }"
    :disabled="disabled"
  >
    <template #button-content>
      <slot
        class="grouped-select__dropdown-content"
        name="content"
        v-bind="selectedOptionObject"
      >
        <span class="grouped-select__dropdown-content">
         {{ selectedOptionTextField }}
        </span>
      </slot>
    </template>
    <b-dropdown-item
      v-for="item in items"
      :key="item[valueField]"
      @click="selectItem(item)"
    >
      <slot name="item" v-bind="item">
        <div> {{ item[textField] || item }} </div>
        <div
          v-for="(child, index) in item[itemsChildName]"
          class="grouped-select__child"
          :key="`child-${index}`"
        >
          {{ child[textField] || child }}
        </div>
      </slot>
    </b-dropdown-item>
    <b-dropdown-group
      v-for="(item, index) in groups"
      :key="`group-${index}`"
      :header="item.label"
    >
      <b-dropdown-item
        v-for="(option, index) in item.options"
        :key="`group-child-${index}`"
        @click="selectItem(option)"
      >
        <slot name="item" v-bind="option">
          <div class="ml-3"> {{ option[textField] || option }} </div>
          <div
            v-for="(child, index) in option[itemsChildName]"
            class="grouped-select__group-child"
            :key="index"
          >
            {{ child[textField] || child }}
          </div>
        </slot>
      </b-dropdown-item>
    </b-dropdown-group>
  </b-dropdown>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import isObjectLike from 'lodash/isObjectLike';
import selectMixin from '@/components/mixins/selectMixin';

export default {
  name: 'GroupedSelect',
  mixins: [
    selectMixin,
  ],
  props: {
    value: {
      type: [String, Boolean, Object, Number],
    },
    options: {
      type: Array,
      default: () => ([]),
    },
    itemsChildName: {
      type: String,
      default: 'children',
    },
    valueField: {
      type: String,
      default: 'value',
    },
    textField: {
      type: String,
      default: 'text',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    state: {
      type: Boolean,
      default: null,
    },
  },
  data: () => ({
    selectedOptionObject: null,
  }),
  computed: {
    groups() {
      return this.options.filter(option => !isEmpty(option.options));
    },
    items() {
      return this.options.filter(option => isEmpty(option.options));
    },
  },
  methods: {
    getDropdownButtonElement() {
      const refDropdown = this.$refs.groupedDropdown;
      if (!refDropdown) return null;
      return refDropdown.$el.firstElementChild;
    },
    getAllOptions() {
      const options = this.options.concat(
        this.options.map(option => (option.options ? [...option.options] : option)),
      );
      const optionsWithoutParents = options.filter(option => isEmpty(option.options));
      const uniqOptions = uniq(optionsWithoutParents);
      return flatten(uniqOptions);
    },
    compareBetweenObjectAndString(firstValue, secondValue) {
      if (typeof firstValue === 'string' && isObjectLike(secondValue)) {
        return secondValue[this.valueField] === firstValue;
      }
      return firstValue[this.valueField] === secondValue;
    },
    compareOptionValueWithItem(option, item) {
      if (option[this.valueField] === undefined) {
        return this.comparePrimitiveValues(item, option);
      }
      const valueToCompare = isObjectLike(item) ? item[this.valueField] : item;
      if (typeof valueToCompare !== typeof option[this.valueField]) {
        return this.compareBetweenObjectAndString(valueToCompare, option[this.valueField]);
      }
      return option[this.valueField] === valueToCompare;
    },
    getValueObject(item) {
      const allOptions = this.getAllOptions();
      return allOptions.find(option => this.compareOptionValueWithItem(option, item));
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/variables"

.grouped-select
  position: relative !important
  width: 100%
  &__invalid ::v-deep
    .btn.dropdown-toggle
      border: 1px solid $color-danger !important
      border-radius: 3px
      &:focus
        border-color: $color-danger !important
        box-shadow: 0 0 0 0.2rem rgba(231, 15, 15, 0.25) !important
  &__dropdown-content-wrapper,
  &__dropdown-content-slot-wrapper
    display: block
    text-overflow: ellipsis
    overflow: hidden
    white-space: nowrap
    width: 95%
    text-align: left
  &__dropdown-content,
  &__dropdown-slot-content
    font-weight: normal
    text-transform: none
    color: var(--font-color-primary)
  &__child
    font-size: .9rem
    color: var(--font-color-primary)
    font-weight: 300
    margin-left: 25px
  ::v-deep
    .dropdown-toggle
      width: inherit
    .dropdown-menu
      width: inherit
      min-width: max-content
      padding: 2px 0
      background-color: var(--color-background-light)
      max-height: 38vh
      overflow-y: auto
      overflow-x: visible
      header
        padding-left: 10px
        font-weight: bold
        font-size: 1rem
        margin-bottom: 0
      .dropdown-item
        padding: 0 10px
        margin-bottom: 0
        cursor: default
      .dropdown-item:hover
        background-color: $color-primary
        color: white
        .grouped-select__child
          color: white
    button
      cursor: default !important
      height: 34px !important
      min-height: unset !important
      padding: 0 10px !important
      border: 1px solid #ced4da
      display: flex
      justify-content: space-between
      align-items: center
      background-color: var(--color-background-light) !important
      &:after
        content: ""
        display: inline-block
        margin-left: 0.255em
        margin-bottom: 1px
        vertical-align: 0.255em
        border-top: 0.5em solid $color-gray-medium
        border-right: 0.4em solid transparent
        border-bottom: 0
        border-left: 0.4em solid transparent
      &:focus
        border-color: #ffb59b !important
        outline: 0 !important
        box-shadow: 0 0 0 0.2rem rgba(255, 86, 27, 0.25) !important
      &.disabled
        background-color: $color-gray-select !important
        span
          color: $font-color-primary
.manage-fonts_18px
  .grouped-select ::v-deep button
    height: 42px !important
.manage-fonts_16px
  .grouped-select ::v-deep button
    height: 38px !important
</style>
